import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    ArrowLeft,
    Download,
    Loader2,
    AlertTriangle,
    CheckCircle2,
    Info,
    ExternalLink,
    RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * /admin/import — one-shot Monday.com → VIRTUS Dev Plan importer.
 * 3 steps: token → pick board → confirm & import.
 */
export default function AdminImport() {
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [token, setToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [boards, setBoards] = useState([]);
    const [selectedBoardId, setSelectedBoardId] = useState("");
    const [preview, setPreview] = useState(null);
    const [target] = useState("dev-plan"); // fixed per user's choice
    const [replace, setReplace] = useState(true);
    const [columnMap, setColumnMap] = useState(null);
    const [result, setResult] = useState(null);
    const [keepSyncing, setKeepSyncing] = useState(true);
    const [intervalMinutes, setIntervalMinutes] = useState(5);

    const fetchBoards = async () => {
        if (!token.trim()) {
            toast.error("Paste your Monday.com API token first.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/migrate/monday/boards", { token: token.trim() });
            setBoards(data.boards || []);
            if ((data.boards || []).length === 0) {
                toast.warning("No boards visible with this token.");
            } else {
                setStep(2);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to list boards.");
        } finally {
            setLoading(false);
        }
    };

    const fetchPreview = async (boardId) => {
        setSelectedBoardId(boardId);
        setLoading(true);
        try {
            const { data } = await api.post("/migrate/monday/preview", {
                token: token.trim(),
                board_id: String(boardId),
            });
            setPreview(data);
            setColumnMap(data.column_map);
            setStep(3);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to preview board.");
        } finally {
            setLoading(false);
        }
    };

    const runImport = async () => {
        setLoading(true);
        setResult(null);
        try {
            const { data } = await api.post("/migrate/monday/import", {
                token: token.trim(),
                board_id: String(selectedBoardId),
                target,
                replace,
                column_map: columnMap,
                keep_syncing: keepSyncing,
                interval_minutes: intervalMinutes,
            });
            setResult(data);
            const syncMsg = keepSyncing ? ` · Auto-syncing every ${intervalMinutes} min.` : "";
            toast.success(
                `Imported ${data.item_count} tasks + ${data.subtask_count} subtasks.${syncMsg}`
            );
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-virtus-bg text-white">
            {/* Top bar */}
            <div className="border-b border-virtus-line px-8 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Link
                        to="/hub"
                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange"
                        data-testid="admin-import-back"
                    >
                        <ArrowLeft size={12} /> HUB
                    </Link>
                    <span className="text-virtus-faint">//</span>
                    <div>
                        <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-virtus-orange">
                            A0/DATA MIGRATION
                        </div>
                        <div className="font-heading text-2xl font-black uppercase tracking-tight text-white mt-1">
                            Import from Monday.com
                        </div>
                    </div>
                </div>
                <StepIndicator step={step} />
            </div>

            <div className="max-w-[1100px] mx-auto px-8 py-8">
                {/* Fixed context */}
                <div className="mb-6 flex items-start gap-3 border border-virtus-line bg-[#0F0F0F] px-4 py-3 text-sm text-virtus-dim">
                    <Info size={16} className="text-virtus-orange flex-shrink-0 mt-0.5" />
                    <div>
                        Imports into <span className="text-virtus-orange">Dev Plan</span> and{" "}
                        <span className="text-virtus-stuck">REPLACES</span> all existing Dev Plan
                        groups and tasks. This runs once — for ongoing sync, tell me and I'll build
                        a re-import button.
                    </div>
                </div>

                {step === 1 && (
                    <TokenStep
                        token={token}
                        setToken={setToken}
                        onNext={fetchBoards}
                        loading={loading}
                    />
                )}

                {step === 2 && (
                    <BoardPickerStep
                        boards={boards}
                        onSelect={fetchPreview}
                        loading={loading}
                        onBack={() => setStep(1)}
                    />
                )}

                {step === 3 && preview && (
                    <PreviewStep
                        preview={preview}
                        columnMap={columnMap}
                        setColumnMap={setColumnMap}
                        replace={replace}
                        setReplace={setReplace}
                        keepSyncing={keepSyncing}
                        setKeepSyncing={setKeepSyncing}
                        intervalMinutes={intervalMinutes}
                        setIntervalMinutes={setIntervalMinutes}
                        onBack={() => setStep(2)}
                        onImport={runImport}
                        loading={loading}
                        result={result}
                        onDone={() => nav("/dev-plan")}
                    />
                )}
            </div>
        </div>
    );
}

function StepIndicator({ step }) {
    const labels = ["Token", "Board", "Import"];
    return (
        <div className="flex items-center gap-3">
            {labels.map((l, i) => {
                const n = i + 1;
                const active = step === n;
                const done = step > n;
                return (
                    <div key={l} className="flex items-center gap-2">
                        <div
                            className={`w-6 h-6 flex items-center justify-center font-mono text-[11px] border ${
                                done
                                    ? "bg-virtus-done text-black border-virtus-done"
                                    : active
                                    ? "bg-virtus-orange text-black border-virtus-orange"
                                    : "text-virtus-dim border-virtus-line"
                            }`}
                        >
                            {done ? <CheckCircle2 size={12} /> : n}
                        </div>
                        <span
                            className={`font-mono text-[10px] tracking-[0.28em] uppercase ${
                                active ? "text-white" : "text-virtus-dim"
                            }`}
                        >
                            {l}
                        </span>
                        {n < labels.length && <span className="text-virtus-line mx-2">/</span>}
                    </div>
                );
            })}
        </div>
    );
}

function TokenStep({ token, setToken, onNext, loading }) {
    return (
        <div className="space-y-6">
            <div>
                <label className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                    Monday.com API token
                </label>
                <textarea
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    rows={3}
                    placeholder="Paste your Monday.com personal API token here (starts with eyJhbGciOi...)"
                    className="mt-2 w-full bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-4 py-3 text-sm font-mono text-white outline-none resize-none"
                    data-testid="admin-import-token-input"
                />
                <a
                    href="https://developer.monday.com/api-reference/docs/authentication#accessing-your-api-token"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange hover:underline"
                >
                    How to find my token <ExternalLink size={10} />
                </a>
                <p className="text-xs text-virtus-dim mt-3 leading-relaxed">
                    Monday → click your avatar (bottom-left) → <b>Developers</b> →{" "}
                    <b>My Access Tokens</b> → <b>Show</b>. Read-only scope is enough. The token is
                    used once for this import and is not stored.
                </p>
            </div>
            <div className="flex justify-end">
                <button
                    onClick={onNext}
                    disabled={loading || !token.trim()}
                    className="flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase bg-virtus-orange text-black px-5 py-3 hover:bg-white disabled:opacity-40 glow-orange"
                    data-testid="admin-import-fetch-boards-btn"
                >
                    {loading ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    LIST MY BOARDS
                </button>
            </div>
        </div>
    );
}

function BoardPickerStep({ boards, onSelect, loading, onBack }) {
    return (
        <div className="space-y-4">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim">
                Pick a board — its content will be pulled into VIRTUS Dev Plan.
            </div>
            <div className="border border-virtus-line divide-y divide-virtus-line">
                {boards.map((b) => (
                    <button
                        key={b.id}
                        onClick={() => onSelect(b.id)}
                        disabled={loading}
                        className="w-full text-left px-5 py-4 hover:bg-[#141414] disabled:opacity-40 flex items-center justify-between group"
                        data-testid={`admin-import-board-${b.id}`}
                    >
                        <div>
                            <div className="text-white font-medium group-hover:text-virtus-orange">
                                {b.name}
                            </div>
                            {b.description && (
                                <div className="text-xs text-virtus-dim mt-1 line-clamp-1">
                                    {b.description}
                                </div>
                            )}
                        </div>
                        <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim group-hover:text-virtus-orange">
                            {b.items_count || 0} ITEMS <ArrowLeft size={11} className="inline rotate-180 ml-2" />
                        </div>
                    </button>
                ))}
            </div>
            <div className="flex items-center justify-between">
                <button
                    onClick={onBack}
                    className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange"
                >
                    ← BACK
                </button>
                {loading && (
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-orange">
                        <Loader2 size={11} className="animate-spin" /> PULLING BOARD…
                    </div>
                )}
            </div>
        </div>
    );
}

function PreviewStep({
    preview,
    columnMap,
    setColumnMap,
    replace,
    setReplace,
    keepSyncing,
    setKeepSyncing,
    intervalMinutes,
    setIntervalMinutes,
    onBack,
    onImport,
    loading,
    result,
    onDone,
}) {
    const targetLabel = "Dev Plan";

    if (result) {
        return (
            <div className="border border-virtus-done bg-virtus-done/10 p-6 space-y-4">
                <div className="flex items-center gap-3">
                    <CheckCircle2 size={24} className="text-virtus-done" />
                    <div className="font-heading text-2xl font-black uppercase tracking-tight text-virtus-done">
                        IMPORT COMPLETE
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-4 font-mono">
                    <StatBlock label="GROUPS" value={result.group_count} />
                    <StatBlock label="TASKS" value={result.item_count} />
                    <StatBlock label="SUBTASKS" value={result.subtask_count} />
                </div>
                <div className="text-sm text-virtus-dim">
                    Imported from{" "}
                    <span className="text-virtus-orange">{result.source_board_name}</span> into{" "}
                    <span className="text-virtus-orange">{targetLabel}</span>.
                </div>
                <button
                    onClick={onDone}
                    className="font-mono text-[11px] tracking-[0.28em] uppercase bg-virtus-orange text-black px-5 py-3 hover:bg-white glow-orange"
                    data-testid="admin-import-goto-devplan-btn"
                >
                    OPEN DEV PLAN →
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-4 gap-4 font-mono">
                <StatBlock label="SOURCE BOARD" value={preview.board_name} big={false} />
                <StatBlock label="GROUPS" value={preview.group_count} />
                <StatBlock label="TASKS" value={preview.item_count} />
                <StatBlock label="SUBTASKS" value={preview.subtask_count} />
            </div>

            <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-3">
                    COLUMN MAPPING (auto-suggested — override if needed)
                </div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                    {["status", "priority", "owner", "start_date", "due_date"].map((field) => (
                        <div key={field} className="flex items-center gap-3">
                            <div className="w-28 font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim">
                                {field.replace("_", " ")}
                            </div>
                            <select
                                value={columnMap[field] || ""}
                                onChange={(e) =>
                                    setColumnMap({ ...columnMap, [field]: e.target.value || null })
                                }
                                className="flex-1 bg-[#0A0A0A] border border-virtus-line focus:border-virtus-orange px-3 py-2 text-white font-mono text-xs outline-none"
                                data-testid={`admin-import-map-${field}`}
                            >
                                <option value="">— none —</option>
                                {(preview.columns || []).map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.title} ({c.type})
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
                </div>
            </div>

            <SamplePreview preview={preview} />

            <div className="flex items-center gap-3 border border-virtus-stuck/50 bg-virtus-stuck/5 px-4 py-3">
                <AlertTriangle size={16} className="text-virtus-stuck flex-shrink-0" />
                <div className="text-sm text-white">
                    Clicking IMPORT will{" "}
                    <span className="text-virtus-stuck font-bold">
                        {replace ? "REPLACE all existing" : "APPEND to existing"}
                    </span>{" "}
                    Dev Plan groups and tasks.
                </div>
                <label className="ml-auto flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim cursor-pointer">
                    <input
                        type="checkbox"
                        checked={replace}
                        onChange={(e) => setReplace(e.target.checked)}
                        className="accent-virtus-orange"
                        data-testid="admin-import-replace-toggle"
                    />
                    Wipe existing first
                </label>
            </div>

            <div className="flex items-center gap-3 border border-virtus-orange/40 bg-virtus-orange/5 px-4 py-3">
                <RefreshCw size={16} className="text-virtus-orange flex-shrink-0" />
                <div className="text-sm text-white flex-1">
                    <div className="font-bold text-virtus-orange">Keep this in sync</div>
                    <div className="text-xs text-virtus-dim mt-0.5">
                        When on, VIRTUS polls Monday and reflects every change (adds/edits/deletes/status) automatically.
                    </div>
                </div>
                <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim">
                    Every
                    <input
                        type="number"
                        min={1}
                        max={60}
                        value={intervalMinutes}
                        onChange={(e) =>
                            setIntervalMinutes(Math.max(1, Math.min(60, Number(e.target.value) || 5)))
                        }
                        disabled={!keepSyncing}
                        className="w-14 bg-[#0A0A0A] border border-virtus-line px-2 py-1 text-white text-xs font-mono outline-none disabled:opacity-40"
                        data-testid="admin-import-sync-interval"
                    />
                    min
                </label>
                <label className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-dim cursor-pointer">
                    <input
                        type="checkbox"
                        checked={keepSyncing}
                        onChange={(e) => setKeepSyncing(e.target.checked)}
                        className="accent-virtus-orange"
                        data-testid="admin-import-keep-syncing-toggle"
                    />
                    Sync ON
                </label>
            </div>

            <div className="flex items-center justify-between">
                <button
                    onClick={onBack}
                    className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-virtus-orange"
                >
                    ← BACK
                </button>
                <button
                    onClick={onImport}
                    disabled={loading}
                    className="flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase bg-virtus-orange text-black px-6 py-3 hover:bg-white disabled:opacity-40 glow-orange"
                    data-testid="admin-import-run-btn"
                >
                    {loading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                    {loading ? "IMPORTING…" : "IMPORT TO DEV PLAN"}
                </button>
            </div>
        </div>
    );
}

function StatBlock({ label, value, big = true }) {
    return (
        <div className="border border-virtus-line bg-[#0A0A0A] px-4 py-3">
            <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-dim">
                {label}
            </div>
            <div className={`text-white mt-1 ${big ? "text-2xl font-bold" : "text-sm"}`}>
                {value}
            </div>
        </div>
    );
}

function SamplePreview({ preview }) {
    const groupIndex = {};
    (preview.groups || []).forEach((g) => (groupIndex[g.monday_id] = g));

    const perGroup = {};
    (preview.items || []).forEach((it) => {
        const key = it.group_id;
        (perGroup[key] = perGroup[key] || []).push(it);
    });

    return (
        <div>
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim mb-3">
                PREVIEW (first 3 tasks per group)
            </div>
            <div className="border border-virtus-line divide-y divide-virtus-line max-h-[400px] overflow-y-auto">
                {(preview.groups || []).map((g) => {
                    const list = (perGroup[g.monday_id] || []).slice(0, 3);
                    return (
                        <div key={g.monday_id} className="px-4 py-3">
                            <div className="flex items-center gap-2">
                                <span
                                    className="inline-block w-2 h-4"
                                    style={{ backgroundColor: g.color }}
                                />
                                <div className="font-mono text-xs tracking-[0.24em] uppercase text-white">
                                    {g.name}
                                </div>
                                <div className="font-mono text-[10px] text-virtus-dim">
                                    [{(perGroup[g.monday_id] || []).length}]
                                </div>
                            </div>
                            {list.length === 0 ? (
                                <div className="mt-2 text-xs text-virtus-faint pl-4">
                                    (no tasks in this group)
                                </div>
                            ) : (
                                <div className="mt-2 pl-4 space-y-1">
                                    {list.map((it) => (
                                        <div
                                            key={it.id}
                                            className="text-xs text-virtus-dim flex items-center gap-3"
                                        >
                                            <span className="text-white truncate flex-1">
                                                {it.name}
                                            </span>
                                            <span className="font-mono uppercase text-[10px] text-virtus-warning">
                                                {it.status}
                                            </span>
                                            <span className="font-mono uppercase text-[10px]">
                                                {it.priority}
                                            </span>
                                            {it.owner && (
                                                <span className="font-mono text-[10px] text-virtus-dim">
                                                    {it.owner}
                                                </span>
                                            )}
                                            {it.subtasks && it.subtasks.length > 0 && (
                                                <span className="font-mono text-[10px] text-virtus-orange">
                                                    +{it.subtasks.length} sub
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
